import type { Mutation, SyncRecordMap, SyncTable } from '@trips/shared/schema';
import Dexie, { type Table } from 'dexie';

/** A queued edit waiting to reach the server. */
export interface OutboxEntry extends Mutation {
  /** Order of creation, for pushing in sequence. */
  seq: number;
  attempts: number;
  lastError?: string;
}

/** A file kept on this device: pending upload, or cached for offline. */
export interface BlobEntry {
  /** `${attachmentId}/${variant}` */
  key: string;
  blob: Blob;
  uploaded: boolean;
  savedAt: number;
}

export interface KvEntry {
  key: string;
  value: unknown;
}

type RecordTables = { [K in SyncTable]: Table<SyncRecordMap[K], string> };

export class TripsDb extends Dexie {
  declare trips: RecordTables['trips'];
  declare items: RecordTables['items'];
  declare places: RecordTables['places'];
  declare attachments: RecordTables['attachments'];
  declare sources: RecordTables['sources'];
  declare itemChanges: RecordTables['itemChanges'];
  declare settings: RecordTables['settings'];
  declare homeBases: RecordTables['homeBases'];
  declare flightLive: RecordTables['flightLive'];
  declare programs: RecordTables['programs'];
  declare cards: RecordTables['cards'];
  declare perks: RecordTables['perks'];
  declare redemptions: RecordTables['redemptions'];
  declare briefs: RecordTables['briefs'];
  declare balanceSnapshots: RecordTables['balanceSnapshots'];
  declare outbox: Table<OutboxEntry, string>;
  declare blobs: Table<BlobEntry, string>;
  declare kv: Table<KvEntry, string>;

  constructor(name = 'trips-app') {
    super(name);
    this.version(1).stores({
      trips: 'id',
      items: 'id, tripId',
      places: 'id',
      attachments: 'id, ownerId',
      sources: 'id',
      itemChanges: 'id, itemId',
      settings: 'id',
      homeBases: 'id',
      flightLive: 'id',
      outbox: 'id, seq',
      blobs: 'key',
      kv: 'key',
    });
    // Wallet: points, cards, perks and redemptions.
    this.version(2).stores({
      programs: 'id',
      cards: 'id',
      perks: 'id',
      redemptions: 'id, programId',
    });
    // News: points briefs.
    this.version(3).stores({ briefs: 'id, date' });
    // Balances over time.
    this.version(4).stores({ balanceSnapshots: 'id, entryId' });
  }

  records<T extends SyncTable>(name: T): RecordTables[T] {
    return this.table(name) as unknown as RecordTables[T];
  }
}

export const db = new TripsDb();

export async function kvGet<T>(key: string): Promise<T | undefined> {
  return (await db.kv.get(key))?.value as T | undefined;
}

export async function kvSet(key: string, value: unknown) {
  await db.kv.put({ key, value });
}
